import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import type { MouseEvent } from 'react';
import { api } from '../../lib/api';
import { bus } from '../../App';
import { formatDateCompact, formatTimeCompact, formatDateTime } from '../../../shared/lib/formatters';
import { useSelectionStore } from '../../state/selectionStore';
import { TagEditor } from '../TagEditor';
import { ToggleButton } from '../ToggleButton';
import { ContextMenu } from '../ContextMenu';
import type { ContextMenuItem } from '../ContextMenu';

interface FileVersion {
  id: number;
  created_at: number;
  fileCount: number;
  major?: boolean;
}

export function VersionsPanel() {
  const [versions, setVersions] = useState<FileVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState<number | null>(null);
  const [taggingVersionId, setTaggingVersionId] = useState<number | null>(null);
  const [tagAnchorEl, setTagAnchorEl] = useState<HTMLElement | null>(null);
  const tagsButtonRef = useRef<HTMLButtonElement | null>(null);
  const [versionTags, setVersionTags] = useState<Record<number, string[]>>({});
  const [taggedVersionIds, setTaggedVersionIds] = useState<Set<number>>(new Set());
  const [contextMenuOpen, setContextMenuOpen] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState<{ x: number; y: number } | null>(null);
  const [contextMenuVersionId, setContextMenuVersionId] = useState<number | null>(null);
  const contextMenuAnchorRef = useRef<HTMLDivElement | null>(null);
  const versionId = useSelectionStore((state) => state.version_id);
  const showMajorOnly = useSelectionStore((state) => state.versions_major);
  const showTaggedOnly = useSelectionStore((state) => state.versions_tagged);
  const select = useSelectionStore((state) => state.set);

  const refreshTaggedIds = useCallback(async () => {
    try {
      const ids = await api().services.tags.getTaggedEntities('version');
      if (Array.isArray(ids)) {
        setTaggedVersionIds(new Set(ids));
      } else {
        setTaggedVersionIds(new Set());
      }
    } catch (error) {
      console.error('[VersionsPanel] Failed to load tagged version IDs:', error);
      setTaggedVersionIds(new Set());
    }
  }, []);

  const refreshTags = useCallback(async () => {
    try {
      const tagged = await api().services.tags.listEntitiesWithTags('version');
      if (Array.isArray(tagged)) {
        const map = tagged.reduce<Record<number, string[]>>((acc, row) => {
          acc[row.entityId] = Array.isArray(row.tags) ? row.tags : [];
          return acc;
        }, {});
        setVersionTags(map);
      } else {
        setVersionTags({});
      }
    } catch (error) {
      console.error('[VersionsPanel] Failed to load version tags:', error);
      setVersionTags({});
    }
    await refreshTaggedIds();
  }, [refreshTaggedIds]);

  const refreshVersions = useCallback(async () => {
    try {
      const versionList = await api().services.files.getVersions();
      setVersions(versionList || []);
    } catch (error) {
      console.error('[VersionsPanel] Failed to load versions:', error);
    }
    await refreshTags();
  }, [refreshTags]);

  useEffect(() => {
    const loadVersions = async () => {
      await refreshVersions();
      setLoading(false);
    };
    loadVersions();
  }, [refreshVersions]);

  useEffect(() => {
    const unsubscribeCreated = bus.on('version/created', async () => {
      await refreshVersions();
    });

    const unsubscribeUpdated = bus.on('version/updated', async () => {
      await refreshVersions();
    });

    return () => {
      unsubscribeCreated();
      unsubscribeUpdated();
    };
  }, [refreshVersions]);

  useEffect(() => {
    const handleTagEvent = async (_event: string, payload?: { entityType?: string }) => {
      if (payload?.entityType === 'version') {
        await refreshTags();
      }
    };

    const unsubscribeAssign = bus.on('tags/assign', handleTagEvent);
    const unsubscribeRemove = bus.on('tags/remove', handleTagEvent);
    const unsubscribeReset = bus.on('tags/reset', async () => {
      await refreshTags();
    });

    return () => {
      unsubscribeAssign();
      unsubscribeRemove();
      unsubscribeReset();
    };
  }, [refreshTags]);

  const selectOldestMessageForVersion = useCallback(async (versionId: number) => {
    try {
      const message = await api().services.context.getMessageForVersion(versionId);
      if (message) {
        select({ message_id: message.id });
      }
    } catch (error) {
      // Silently ignore - it's ok if there's no message for this version
    }
  }, [select]);

  const handleRowSelect = useCallback((version: FileVersion) => {
    select({ version_id: version.id });
    void selectOldestMessageForVersion(version.id);
  }, [select, selectOldestMessageForVersion]);

  const handleContextMenu = useCallback((event: MouseEvent<HTMLLIElement>, version: FileVersion) => {
    event.preventDefault();
    event.stopPropagation();

    // Position the anchor div BEFORE opening the menu
    if (contextMenuAnchorRef.current) {
      contextMenuAnchorRef.current.style.left = `${event.clientX}px`;
      contextMenuAnchorRef.current.style.top = `${event.clientY}px`;
    }

    setContextMenuVersionId(version.id);
    setContextMenuPosition({ x: event.clientX, y: event.clientY });
    setContextMenuOpen(true);
  }, []);

  const closeContextMenu = useCallback(() => {
    setContextMenuOpen(false);
    setContextMenuPosition(null);
    setContextMenuVersionId(null);
  }, []);

  const handleCheckout = async (versionId: number) => {
    setCheckingOut(versionId);
    try {
      await api().services.files.checkout(versionId);
      console.log(`[VersionsPanel] Checked out version ${versionId}`);
    } catch (error) {
      console.error('[VersionsPanel] Failed to checkout version:', error);
    } finally {
      setCheckingOut(null);
    }
  };
  const handleHost = async (versionId: number) => {
    try {
      await api().services.meta.set('host_version_id', versionId);
      console.log(`[VersionsPanel] Set host_version_id to ${versionId}`);
    } catch (error) {
      console.error('[VersionsPanel] Failed to set host version:', error);
    }
  };

  const openTagEditor = useCallback((versionId: number, anchorEl: HTMLElement) => {
    setTaggingVersionId(versionId);
    setTagAnchorEl(anchorEl);
  }, []);

  const closeTagEditor = useCallback(() => {
    setTaggingVersionId(null);
    setTagAnchorEl(null);
  }, []);

  const versionsById = useMemo(() => {
    const map = new Map<number, FileVersion>();
    for (const version of versions) {
      map.set(version.id, version);
    }
    return map;
  }, [versions]);

  const selectedVersion = typeof versionId === 'number' ? versionsById.get(versionId) ?? null : null;

  const filteredVersions = useMemo(() => {
    let filtered = versions;

    if (showMajorOnly) {
      filtered = filtered.filter(v => v.major);
    }

    if (showTaggedOnly) {
      filtered = filtered.filter(v => taggedVersionIds.has(v.id));
    }

    return filtered;
  }, [versions, showMajorOnly, showTaggedOnly, taggedVersionIds]);

  const versionsWithMeta = useMemo(() => {
    return filteredVersions.map((version) => {
      const isoDate = new Date(version.created_at).toISOString();
      return {
        version,
        formattedDisplay: `${formatDateCompact(isoDate, '—')} ${formatTimeCompact(isoDate, '—')}`,
        formattedDateFull: formatDateTime(isoDate, '—'),
      };
    });
  }, [filteredVersions]);

  const contextMenuItems: ContextMenuItem[] = useMemo(() => {
    if (contextMenuVersionId === null) {
      return [];
    }
    const versionIdToUse = contextMenuVersionId;
    return [
      {
        label: 'Tags',
        value: 'tags',
        onClick: () => {
          // The ContextMenu will call closeContextMenu via onClose after this
          // Use setTimeout to open tag editor after context menu closes
          const anchorEl = contextMenuAnchorRef.current;
          if (anchorEl) {
            setTimeout(() => {
              openTagEditor(versionIdToUse, anchorEl);
            }, 0);
          }
        }
      },
      {
        label: 'Host',
        value: 'host',
        onClick: () => {
          void handleHost(versionIdToUse);
        }
      },
      {
        label: 'Checkout',
        value: 'checkout',
        onClick: () => {
          void handleCheckout(versionIdToUse);
        }
      }
    ];
  }, [contextMenuVersionId, openTagEditor]);

  if (loading) {
    return (
      <div className="panel-shell">
        <div className="panel-content panel-content--horizontal">
          <div className="empty-state">
            <p>Loading versions...</p>
          </div>
        </div>
      </div>
    );
  }

  if (versions.length === 0) {
    return (
      <div className="panel-shell">
        <div className="panel-content panel-content--horizontal">
          <div className="empty-state">
            <p>No versions yet</p>
            <p className="empty-state-subtitle">File versions will appear here after snapshots are created</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="panel-shell">
      <div className="panel-controls">
        <ToggleButton
          label="major"
          enabled={showMajorOnly}
          onToggle={(value) => select({ versions_major: value })}
        />
        <ToggleButton
          label="tagged"
          enabled={showTaggedOnly}
          onToggle={(value) => select({ versions_tagged: value })}
        />
        <button
          type="button"
          className="btn"
          onClick={() => {
            if (selectedVersion) {
              void handleHost(selectedVersion.id);
            }
          }}
          disabled={!selectedVersion}
        >
          Host
        </button>
        <button
          type="button"
          className="btn"
          onClick={() => {
            if (selectedVersion) {
              void handleCheckout(selectedVersion.id);
            }
          }}
          disabled={!selectedVersion || checkingOut === selectedVersion?.id}
        >
          {checkingOut === selectedVersion?.id ? 'Checking out…' : 'Checkout'}
        </button>
        <button
          type="button"
          className="btn"
          ref={tagsButtonRef}
          onClick={() => {
            if (selectedVersion && tagsButtonRef.current) {
              openTagEditor(selectedVersion.id, tagsButtonRef.current);
            }
          }}
          disabled={!selectedVersion}
        >
          tags
        </button>
      </div>
      <div className="panel-content panel-content--flush">
        <ul className="versions-list">
          {versionsWithMeta.map(({ version, formattedDisplay, formattedDateFull }, index) => {
            const isSelected = version.id === versionId;
            const tags = versionTags[version.id] ?? [];
            return (
              <li
                key={version.id}
                className={`version-row ${index % 2 === 0 ? 'version-row--even' : 'version-row--odd'}${
                  isSelected ? ' version-row--selected' : ''
                }`}
                onClick={() => handleRowSelect(version)}
                onContextMenu={(event) => handleContextMenu(event, version)}
              >
                <span className="version-row-id">#{version.id}</span>
                <span className="version-row-date" title={formattedDateFull}>{formattedDisplay}</span>
                <span className="version-row-files">
                  {version.fileCount} {version.fileCount === 1 ? 'file' : 'files'}
                </span>
                <div className="version-row-badges">
                  {!!version.major && <span className="badge badge--positive">major</span>}
                  {tags.length > 0 && tags.map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      className="tag-badge tag-badge--assigned tag-badge--interactive"
                      onClick={(event) => {
                        event.stopPropagation();
                        openTagEditor(version.id, event.currentTarget);
                      }}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
      {taggingVersionId !== null && tagAnchorEl && (
        <TagEditor
          entityType="version"
          entityId={taggingVersionId}
          onClose={closeTagEditor}
          anchorEl={tagAnchorEl}
        />
      )}
      <div
        ref={contextMenuAnchorRef}
        style={{
          position: 'fixed',
          width: '1px',
          height: '1px',
          pointerEvents: 'none',
          visibility: 'hidden'
        }}
      />
      <ContextMenu
        items={contextMenuItems}
        isOpen={contextMenuOpen}
        onClose={closeContextMenu}
        anchorEl={contextMenuAnchorRef.current}
        placement="bottom-left"
      />
    </div>
  );
}
